/**
 * Presentation maths for the admin Funnel view. Pure so the awkward cases (an
 * empty cohort, a step nobody reached) are pinned by tests rather than
 * discovered as `NaN%` on the page.
 */
import type {
	AdminFunnelActionType,
	AdminFunnelExcluded,
	AdminFunnelPlatform,
	AdminFunnelStep,
	AdminFunnelSteps,
	AdminFunnelTripOrigin,
	AdminFunnelTripSplit,
} from '@orbit/api-types'
import { barWidth, formatShare } from '../Onboarding/onboardingView'

/** The platform toggle's options, mirroring the wire enum (pinned by a contract test). */
export const PLATFORM_OPTIONS: { value: AdminFunnelPlatform; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'ios', label: 'iOS' },
	{ value: 'android', label: 'Android' },
	{ value: 'web', label: 'Web' },
]

export const ACTION_LABELS: Record<AdminFunnelActionType, string> = {
	itinerary: 'Itinerary',
	flight: 'Flight',
	stay: 'Stay',
	expense: 'Expense',
	idea: 'Idea',
	payUp: 'Pay-up',
	message: 'Message',
}

export const TRIP_ORIGIN_LABELS: Record<AdminFunnelTripOrigin, string> = {
	created: 'Created',
	joined: 'Joined',
}

export interface FunnelRow {
	key: AdminFunnelStep
	label: string
	count: number
	/** Share of the step before; `null` on the first step, which has no step before. */
	conversion: string | null
	/** How many the step before lost here; `null` on the first step. */
	dropOff: number | null
	/** Bar length as a percentage of the first step's. */
	width: number
}

/** The four steps in order, mirroring the wire enum (pinned by a contract test). */
export const FUNNEL_STEPS: { key: AdminFunnelStep; label: string }[] = [
	{ key: 'newDevices', label: 'New devices' },
	{ key: 'signedUp', label: 'Signed up' },
	{ key: 'hasTrip', label: 'Has a trip' },
	{ key: 'activated', label: 'Activated' },
]

/** The four steps in order, each measured against the one before it. */
export const funnelRows = (steps: AdminFunnelSteps): FunnelRow[] =>
	FUNNEL_STEPS.map(({ key, label }, index) => {
		const count = steps[key]
		const previous = index === 0 ? null : steps[FUNNEL_STEPS[index - 1].key]
		return {
			key,
			label,
			count,
			conversion: previous === null ? null : formatShare(count, previous),
			dropOff: previous === null ? null : previous - count,
			width: barWidth(count, steps.newDevices),
		}
	})

/**
 * Bar widths for the two parts of the trip step, each against the first step, so
 * side by side they fill exactly as much of the row as the step's own bar would.
 */
export const splitWidths = (split: AdminFunnelTripSplit, newDevices: number): AdminFunnelTripSplit => ({
	created: barWidth(split.created, newDevices),
	joined: barWidth(split.joined, newDevices),
})

/**
 * A version row's label. Web builds reported `dev` until deploys started stamping
 * the release tag (#252), so those rows can't be told apart by release.
 */
export const versionLabel = (platform: string, version: string): string =>
	platform === 'web' && version === 'dev' ? 'Untracked' : version

/**
 * How much of the range has device data: `none` when it ends before devices were
 * first recorded, `partial` when it starts before and ends on or after that day.
 * All three dates are `YYYYMMDD`, which order as strings.
 */
export const deviceDataCoverage = (
	from: string,
	to: string,
	deviceDataStart: string,
): 'none' | 'partial' | 'full' => {
	if (to < deviceDataStart) return 'none'
	return from < deviceDataStart ? 'partial' : 'full'
}

/**
 * One line per reason the funnel left devices out, so the page says what it is
 * not counting. Reasons that left nothing out are skipped.
 */
export const excludedNotes = (excluded: AdminFunnelExcluded, windowDays: number): string[] => {
	const notes: string[] = []
	if (excluded.maturing > 0) {
		const one = excluded.maturing === 1
		notes.push(
			`${excluded.maturing.toLocaleString()} ${one ? 'device' : 'devices'} first seen in the last ` +
				`${windowDays} days, still inside ${one ? 'its' : 'their'} window`,
		)
	}
	if (excluded.returning > 0) {
		const one = excluded.returning === 1
		notes.push(
			`${excluded.returning.toLocaleString()} returning ${one ? 'user' : 'users'} on a new device`,
		)
	}
	if (excluded.bots > 0) {
		notes.push(`${excluded.bots.toLocaleString()} bot ${excluded.bots === 1 ? 'visit' : 'visits'}`)
	}
	return notes
}

/** A `platform` search param, falling back to every platform when it is missing or unknown. */
export const platformFromParam = (raw: string | null): AdminFunnelPlatform =>
	PLATFORM_OPTIONS.find(option => option.value === raw)?.value ?? 'all'

/** A `:step` route param, or null when it names no step. */
export const stepFromParam = (raw: string | undefined): AdminFunnelStep | null =>
	FUNNEL_STEPS.find(step => step.key === raw)?.key ?? null

/** The platform as search params: none for every platform, so the plain URL stays the default. */
export const platformSearch = (platform: AdminFunnelPlatform): Record<string, string> =>
	platform === 'all' ? {} : { platform }

const platformQuery = (platform: AdminFunnelPlatform): string => {
	const query = new URLSearchParams(platformSearch(platform)).toString()
	return query === '' ? '' : `?${query}`
}

/** The funnel narrowed to `platform`. */
export const funnelPath = (platform: AdminFunnelPlatform): string => `/admin/funnel${platformQuery(platform)}`

/** The list behind one step count, on the same platform. */
export const stepListPath = (step: AdminFunnelStep, platform: AdminFunnelPlatform): string =>
	`/admin/funnel/${step}${platformQuery(platform)}`

/** What a step's list counts: devices on the first step, people after it. */
export const stepCountLabel = (step: AdminFunnelStep, count: number): string => {
	const [one, many] = step === 'newDevices' ? ['device', 'devices'] : ['person', 'people']
	return `${count.toLocaleString()} ${count === 1 ? one : many}`
}

/**
 * How long after the device's first open a step happened, both as Unix seconds:
 * "+45m", "+3h 20m", "+2d 4h". Under a minute reads as "at first open", which also
 * covers an account the sign-in grace let predate its device by a few minutes.
 */
export const formatElapsed = (firstSeenAt: number, at: number): string => {
	const minutes = Math.floor((at - firstSeenAt) / 60)
	if (minutes < 1) return 'at first open'
	if (minutes < 60) return `+${minutes}m`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return minutes % 60 === 0 ? `+${hours}h` : `+${hours}h ${minutes % 60}m`
	const days = Math.floor(hours / 24)
	return hours % 24 === 0 ? `+${days}d` : `+${days}d ${hours % 24}h`
}
